import { useEffect, useState } from "react";
import { StepIndicator } from "~/components/plans/components/StepIndicator";
import { useUser } from "~/hooks/use-user";
import type { CheckoutProfileResult } from "~/routes/api.events.$eventId.checkout-profile";
import { checkoutContactEmailSchema } from "~/utils/contact-email";
import { CheckoutQuestionStep, CheckoutTextField } from "./CheckoutQuestionStep";
import { useCheckoutSession } from "./CheckoutSession";

type Props = {
  eventId: string;
  requiresApproval?: boolean;
  onBack: () => void;
};

const SAVE_ERROR =
  "We couldn't save your email address. Check your connection and try again.";

export function CheckoutEmailStep({
  eventId,
  requiresApproval = false,
  onBack,
}: Props) {
  const { user } = useUser();
  const checkout = useCheckoutSession();
  const [contactEmail, setContactEmail] = useState(
    checkout.draft.contactEmail || user?.contactEmail || "",
  );
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const updateDraft = checkout.update;

  useEffect(() => {
    updateDraft({ contactEmail });
  }, [contactEmail, updateDraft]);

  const saveEmail = async () => {
    if (loading) return;

    const validation = checkoutContactEmailSchema.safeParse(contactEmail);
    if (!validation.success) {
      setFieldError(validation.error.issues[0]?.message);
      setError(null);
      return;
    }

    setLoading(true);
    setFieldError(undefined);
    setError(null);

    if (!await checkout.flush() || !checkout.canContinue()) {
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("intent", "save-email");
    formData.append("contactEmail", validation.data);

    try {
      const response = await fetch(
        `/api/events/${eventId}/checkout-profile`,
        { method: "POST", body: formData },
      );
      const result = (await response.json()) as CheckoutProfileResult;

      if (!checkout.canContinue()) {
        setLoading(false);
        return;
      }

      if (!response.ok || !result.ok) {
        const contactEmailError = result.fieldErrors?.contactEmail;
        setFieldError(contactEmailError);
        setError(contactEmailError ? null : SAVE_ERROR);
        setLoading(false);
        return;
      }

      await checkout.refreshUser("payment");
    } catch {
      if (!checkout.canContinue()) {
        setLoading(false);
        return;
      }
      setError(SAVE_ERROR);
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0">
        <StepIndicator
          currentStep={2}
          showTicketStep
          stepActions={loading ? undefined : { 1: () => checkout.setStep("tickets") }}
        />
      </div>

      <CheckoutQuestionStep
        title="Where should we send your ticket?"
        description={requiresApproval
          ? "If your request is approved, we’ll send your ticket here."
          : "We’ll email you a link to your ticket once it’s confirmed."}
        onSubmit={saveEmail}
        onBack={onBack}
        loading={loading}
        error={error}
      >
        <CheckoutTextField
          label="Email address"
          type="email"
          autoComplete="email"
          inputMode="email"
          maxLength={254}
          placeholder="luca.butterman@joinbutter.co"
          value={contactEmail}
          onChange={(event) => {
            setContactEmail(event.target.value);
            setFieldError(undefined);
            setError(null);
          }}
          error={fieldError}
        />
      </CheckoutQuestionStep>
    </div>
  );
}
